import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import { ApiError, LastEvaluatedKey } from '~/types';

class ApiErrorClass extends Error {
  status: number;
  error_msg: string;
  error_code?: number;

  constructor(status: number, error_msg: string, error_code?: number) {
    super(error_msg);
    this.status = status;
    this.error_msg = error_msg;
    this.error_code = error_code;
  }
}

export type ApiResponse<T extends { data?: any }> = {
  status: number;
  data: T extends { data: any } ? T['data'] : T;
  data_additional?: {
    total?: number;
    last_evaluated_key?: LastEvaluatedKey;
  } | null;
};

export const DEFAULT_SERVER_ERR_MESSAGE = 'Server Error';

// 공통 API 요청 함수 (data 한 번 까기)
export const fetcher = async <T extends { data?: any }>(
  apiRequestFn: () => Promise<AxiosResponse<T>>,
): Promise<ApiResponse<T>> => {
  try {
    const response = await apiRequestFn();

    const additionalData = {
      total:
        'total' in response.data ? (response?.data.total as number) : undefined,
      lastEvaluatedKey:
        'last_evaluated_key' in response.data
          ? (response?.data.last_evaluated_key as LastEvaluatedKey)
          : undefined,
    };

    return {
      status: response.status,
      data: response.data.data,
      data_additional: additionalData ?? null,
    };
  } catch (err) {
    const error = err as ApiError;

    const reqFnString = apiRequestFn.toString();

    const endpoint = reqFnString
      .split(/[`"]/)
      .find((part) => part.startsWith('/'));

    const HTTPMethodRegex = /\.(get|post|delete|put)\b/;
    const HTTPMethod = reqFnString
      .match(HTTPMethodRegex)?.[1]
      .toString()
      .toUpperCase();

    const serverErrorObj = new Error(
      `[Server Error] request: ${HTTPMethod} ${endpoint}, error response: ${JSON.stringify(
        error,
      )}`,
    );

    serverErrorObj.name = 'API Server Error';

    // API 관련 에러는 fetcher에서 전역적으로 처리
    Sentry.captureException(serverErrorObj);

    throw new ApiErrorClass(
      error.status,
      error.body?.message ?? DEFAULT_SERVER_ERR_MESSAGE,
      error.body?.code,
    );
  }
};
